<template>
  <section
    class="flex flex-col overflow-hidden rounded-md border border-gray-200 bg-white text-sm text-gray-900"
  >
    <button
      type="button"
      class="hover:bg-obsidian-50/70 flex w-full items-start gap-3 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      :aria-expanded="mailExpanded"
      :aria-controls="mailPanelId"
      :aria-label="mailExpanded ? t('collapseMailDetails') : t('expandMailDetails')"
      @click="mailExpanded = !mailExpanded"
    >
      <div class="min-w-0 flex-1 space-y-1">
        <p v-if="mail.templateKey" class="font-mono text-[11px] text-gray-500">
          {{ mail.templateKey }}
        </p>
        <h3 class="text-sm font-semibold leading-snug text-obsidian-900">
          {{ mail.subject || t('noSubject') }}
        </h3>
        <p v-if="mail.to" class="truncate text-sm text-obsidian-500">
          <span class="font-medium">{{ t('to') }}</span>
          {{ mail.to }}
        </p>
      </div>
      <ChevronDownIcon
        class="mt-0.5 h-5 w-5 shrink-0 text-obsidian-500 transition-transform duration-200"
        :class="{ 'rotate-180': mailExpanded }"
        aria-hidden="true"
      />
    </button>

    <div
      v-show="mailExpanded"
      :id="mailPanelId"
      class="space-y-4 border-t border-obsidian-100 px-4 pb-4 pt-3"
    >
      <div v-if="mail.from || mail.cc || mail.bcc" class="space-y-0.5 text-sm">
        <p v-if="mail.from" class="text-obsidian-500">
          <span class="font-medium">{{ t('from') }}</span>
          {{ mail.from }}
        </p>
        <p v-if="mail.cc" class="text-obsidian-500">
          <span class="font-medium">{{ t('cc') }}</span>
          {{ mail.cc }}
        </p>
        <p v-if="mail.bcc" class="text-obsidian-500">
          <span class="font-medium">{{ t('bcc') }}</span>
          {{ mail.bcc }}
        </p>
      </div>

      <div v-if="hasAnyBodyContent" class="space-y-2">
        <Tabs
          v-if="showContentTabs"
          v-model="contentTab"
          :tabs="bodyTabs"
          :sync-route="false"
          class="mb-1"
        />

        <div
          v-if="contentTab === 'html' && mail.htmlBodyUrl"
          class="overflow-hidden rounded-md border border-gray-200 bg-white"
        >
          <iframe
            class="h-96 min-h-[14rem] w-full border-0"
            sandbox=""
            referrerpolicy="no-referrer"
            :title="t('tabHtml')"
            :src="mail.htmlBodyUrl"
          />
        </div>
        <p
          v-else-if="contentTab === 'html'"
          class="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500"
        >
          {{ t('bodyNotAvailable') }}
        </p>
        <iframe
          v-if="contentTab === 'text' && mail.textBodyUrl"
          class="h-96 min-h-[14rem] w-full rounded-md border border-gray-200 bg-white"
          sandbox=""
          referrerpolicy="no-referrer"
          :title="t('tabPlainText')"
          :src="mail.textBodyUrl"
        />
        <p
          v-else-if="contentTab === 'text'"
          class="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500"
        >
          {{ t('bodyNotAvailable') }}
        </p>
      </div>

      <section v-if="mail.attachments?.length">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {{ t('attachments') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <template v-for="(att, attIdx) in mail.attachments" :key="`${att.fileKey}-${attIdx}`">
            <a
              v-if="att.downloadUrl"
              :href="att.downloadUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="border-violet-200 bg-violet-50 text-violet-800 hover:border-violet-300 hover:bg-violet-100 inline-flex max-w-full items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors"
              :title="att.filename"
            >
              <PaperClipIcon class="text-violet-600 mr-1 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span class="truncate">{{ att.filename }}</span>
            </a>
            <span
              v-else
              class="inline-flex max-w-full items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-mono text-[11px] text-gray-700"
              :title="att.filename"
            >
              <PaperClipIcon class="mr-1 h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
              <span class="truncate">{{ att.filename }}</span>
            </span>
          </template>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { SentCustomerMailMeta } from '@carvia/ros-client-types';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { PaperClipIcon } from '@heroicons/vue/20/solid';
import { computed, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  mail: SentCustomerMailMeta;
}>();

const { t } = useI18n();

const mailPanelId = useId();
const mailExpanded = ref(false);
const contentTab = ref('html');

const bodyTabs = computed<TabItem[]>(() => [
  { id: 'html', title: t('tabHtml') },
  { id: 'text', title: t('tabPlainText') },
]);

const showContentTabs = computed(() => Boolean(props.mail.htmlBodyUrl && props.mail.textBodyUrl));

const hasAnyBodyContent = computed(() =>
  Boolean(
    props.mail.htmlBodyKey ||
      props.mail.textBodyKey ||
      props.mail.htmlBodyUrl ||
      props.mail.textBodyUrl,
  ),
);

watch(
  () => [props.mail.htmlBodyUrl, props.mail.textBodyUrl],
  () => {
    contentTab.value = props.mail.htmlBodyUrl ? 'html' : 'text';
  },
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "from": "From:",
    "to": "To:",
    "cc": "Cc:",
    "bcc": "Bcc:",
    "noSubject": "(No subject)",
    "tabHtml": "HTML",
    "tabPlainText": "Plain text",
    "expandMailDetails": "Show mail details",
    "collapseMailDetails": "Hide mail details",
    "bodyNotAvailable": "Message body is not available (stored payload missing or link expired).",
    "attachments": "Attachments"
  },
  "de": {
    "from": "Von:",
    "to": "An:",
    "cc": "Cc:",
    "bcc": "Bcc:",
    "noSubject": "(Kein Betreff)",
    "tabHtml": "HTML",
    "tabPlainText": "Klartext",
    "expandMailDetails": "E-Mail-Details anzeigen",
    "collapseMailDetails": "E-Mail-Details ausblenden",
    "bodyNotAvailable": "Nachrichtentext ist nicht verfügbar (fehlende Daten oder abgelaufener Link).",
    "attachments": "Anhänge"
  },
  "es": {
    "from": "De:",
    "to": "Para:",
    "cc": "Cc:",
    "bcc": "Cco:",
    "noSubject": "(Sin asunto)",
    "tabHtml": "HTML",
    "tabPlainText": "Texto plano",
    "expandMailDetails": "Mostrar detalles del correo",
    "collapseMailDetails": "Ocultar detalles del correo",
    "bodyNotAvailable": "El cuerpo del mensaje no está disponible (faltan datos o el enlace ha caducado).",
    "attachments": "Adjuntos"
  }
}
</i18n>
